import { normalizarNombre } from "../catalogo/normalizar";

// ============================================================
// Copiloto del mostrador — etapa 1 (Kevin, 16-oct-2026): un panel SIEMPRE visible en el punto de
// venta, desde que empieza la atención, con
//   · las preguntas que quien atiende tiene que hacer (alergias, medicamentos de los últimos días,
//     las de rutina) — en la etapa 1 son solo RECORDATORIO: no se guarda ninguna respuesta;
//   · las recomendaciones de uso de lo que se lleva, amarradas al PRINCIPIO ACTIVO: se escribe una
//     vez para «paracetamol» y sale para todas sus marcas y genéricos.
// El contenido lo escribe la botica (Dueño o Encargado) en Panel › Copiloto: el sistema no inventa
// indicaciones clínicas. La etapa 2 (registrar al cliente, con consentimiento) espera a las cámaras.
// ============================================================

export const MAX_PREGUNTA = 200;
export const MAX_RECOMENDACION = 400;
export const MAX_PRINCIPIO_ACTIVO = 120;

export type PreguntaCopiloto = { id: string; texto: string; orden: number };
export type RecomendacionCopiloto = { id: string; principio_activo: string; texto: string };

/** Lo mínimo que el panel necesita de cada producto del carrito. */
export type ProductoCopiloto = { producto_id: string; nombre: string; principio_activo: string | null };

export type RecomendacionParaProducto = { producto_id: string; nombre: string; recomendaciones: RecomendacionCopiloto[] };

/**
 * ¿Esta recomendación vale para este principio activo? Por CONTENIDO normalizado, como las reglas de
 * sugerencia: en el catálogo el principio activo viene con la concentración pegada («Paracetamol 500 mg»
 * o «PARACETAMOL»), así que una recomendación escrita «paracetamol» tiene que valer igual.
 */
export function aplicaRecomendacion(r: RecomendacionCopiloto, principioActivo: string | null): boolean {
  const pa = principioActivo ? normalizarNombre(principioActivo) : "";
  const clave = normalizarNombre(r.principio_activo);
  return pa.length > 0 && clave.length > 0 && pa.includes(clave);
}

/**
 * Recomendaciones para lo que hay en el carrito, en el orden del carrito y un producto una sola vez.
 * Los productos sin ninguna recomendación no salen (el panel dice aparte que no hay nada que agregar).
 */
export function recomendacionesParaCarrito(recs: RecomendacionCopiloto[], productos: ProductoCopiloto[]): RecomendacionParaProducto[] {
  const vistos = new Set<string>();
  const salen: RecomendacionParaProducto[] = [];
  for (const p of productos) {
    if (vistos.has(p.producto_id)) continue;
    vistos.add(p.producto_id);
    const propias = recs.filter((r) => aplicaRecomendacion(r, p.principio_activo));
    if (propias.length) salen.push({ producto_id: p.producto_id, nombre: p.nombre, recomendaciones: propias });
  }
  return salen;
}

/** Error legible (es-PE) de una pregunta, o `null` si vale. Lo usan el endpoint y el formulario. */
export function errorDePregunta(texto: string): string | null {
  const t = (texto ?? "").trim();
  if (!t) return "la pregunta no puede estar vacía";
  if (t.length > MAX_PREGUNTA) return `la pregunta no puede pasar de ${MAX_PREGUNTA} caracteres`;
  return null;
}

/** Error legible (es-PE) de una recomendación, o `null` si vale. */
export function errorDeRecomendacion(principioActivo: string, texto: string): string | null {
  const pa = (principioActivo ?? "").trim();
  if (!pa) return "falta el principio activo";
  if (pa.length > MAX_PRINCIPIO_ACTIVO) return `el principio activo no puede pasar de ${MAX_PRINCIPIO_ACTIVO} caracteres`;
  const t = (texto ?? "").trim();
  if (!t) return "la recomendación no puede estar vacía";
  if (t.length > MAX_RECOMENDACION) return `la recomendación no puede pasar de ${MAX_RECOMENDACION} caracteres`;
  return null;
}
